import { z } from 'zod';

// Per-organization payment-zone catalog (organizations.payment_zone). Drives
// the Billing Zone dropdown on the itinerary settings page. The DB row also
// carries `key`, `secret`, `webhookToken` — payment-provider credentials that
// MUST NOT be exposed through the read endpoint. Only `id` + `name` cross
// the wire here. Mutations / credential admin land on a future router.
export const paymentZoneItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export type PaymentZoneItem = z.infer<typeof paymentZoneItemSchema>;

export const findPaymentZonesResultSchema = z.object({
  data: z.array(paymentZoneItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
  /**
   * The DMC's own country (its country of incorporation, by name): a new
   * itinerary falls back to the zone named after it (XT-928).
   */
  homeCountry: z.string().nullable().optional(),
});

export type FindPaymentZonesResult = z.infer<
  typeof findPaymentZonesResultSchema
>;

// ─── Admin credential management ──────────────────────────────────────────
// The Stripe credentials (publishable key, secret key, webhook signing
// secret) are write-only over the wire: a caller can SET them but the API
// never returns the plaintext. The list/detail reads expose only whether each
// credential is configured, never the value itself.

export const paymentZoneAdminItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  hasKey: z.boolean(),
  hasSecret: z.boolean(),
  hasWebhookToken: z.boolean(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});
export type PaymentZoneAdminItem = z.infer<typeof paymentZoneAdminItemSchema>;

export const findAllPaymentZonesAdminResultSchema = z.object({
  data: z.array(paymentZoneAdminItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindAllPaymentZonesAdminResult = z.infer<
  typeof findAllPaymentZonesAdminResultSchema
>;

export const createPaymentZoneInput = z.object({
  name: z.string().min(1),
  // Stripe publishable key, secret key, webhook signing secret. Stored
  // encrypted (aes-256-ctr) — never returned in plaintext.
  key: z.string().min(1),
  secret: z.string().min(1),
  webhookToken: z.string().min(1),
});
export type CreatePaymentZoneInput = z.infer<typeof createPaymentZoneInput>;

export const updatePaymentZoneInput = z
  .object({
    id: z.uuid(),
    name: z.string().min(1).optional(),
    // Credentials are optional on update — omit to leave a credential
    // unchanged; pass a new value to rotate it.
    key: z.string().min(1).optional(),
    secret: z.string().min(1).optional(),
    webhookToken: z.string().min(1).optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== 'id').length > 0, {
    message: 'At least one field must be provided',
  });
export type UpdatePaymentZoneInput = z.infer<typeof updatePaymentZoneInput>;

export const deletePaymentZoneInput = z.object({ id: z.uuid() });
export type DeletePaymentZoneInput = z.infer<typeof deletePaymentZoneInput>;

export const deletePaymentZoneResultSchema = z.object({ id: z.uuid() });
export type DeletePaymentZoneResult = z.infer<
  typeof deletePaymentZoneResultSchema
>;
